import React, {useState, useEffect, useMemo}		from 'react';
import {useNavigate, useParams}						from 'react-router-dom';
import moment										from 'moment';
import {useReactTable, ColumnDef, getCoreRowModel,
	getFilteredRowModel, getSortedRowModel, getFacetedRowModel,
	getFacetedUniqueValues, getFacetedMinMaxValues} from '@tanstack/react-table';

import DevicesDocumentsMenu							from './DevicesDocuments';
import DevicesHistorical							from './DevicesHistorical';
import DevicesCharacteristicForm					from './DevicesCharacteristicForm';
import {DeviceStatus}								from '../ressources/components/Device';
import Badge										from '../ressources/components/Badge';
import Button										from '../ressources/components/Button';
import Input										from '../ressources/components/Input';
import Select										from '../ressources/components/Select';
import MultiInputContainer							from '../ressources/components/Input/MultiInputContainer';
import Tabs											from '../ressources/components/Tabs';
import Icon											from '../ressources/components/Icon';
import {IconModalSave}								from '../ressources/components/Icon/preConfig';
import Modal										from '../ressources/components/Modal';
import Load											from '../ressources/components/Load/Load';
import {FilterChecked}								from '../ressources/components/Modal/interface';
import ModalFilter									from '../ressources/components/ModalFilter';
import Section										from '../ressources/components/Section';
import ButtonStickyContainer						from '../ressources/components/Button/ButtonStickyContainer';
import DeviceImage									from '../ressources/components/Device/DeviceImage';
import ModalTags									from '../ressources/components/ModalTags';
import ModalAssign									from '../ressources/components/ModalAssign';
import ModalMdmCommand								from '../ressources/components/ModalMdmCommand';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {assignDevicesApi, deleteDevicesApi, getDevicesApi,
	getMdmDevicesApi, putDevicesApi}				from '../redux/devices/devices';
import {getCompanyUsersApi}							from '../redux/companyUsers/companyUsers';
import {getContractsApi}							from '../redux/contracts/contracts';
import {getSupportsApi}								from '../redux/support/support';
import {Device, DevicesState, PutDeviceData}		from '../redux/devices/interface';
import {SelectData, SelectValues} 					from '../ressources/components/Select/interface';
import {TabsData, TabsValues}						from '../ressources/components/Tabs/interface';
import {Contract, Funder}							from '../redux/contracts/interface';
import {InputValues}								from '../ressources/components/Input/interface';
import {CatalogInfo} 								from '../redux/catalog/interface';
import {LanguageType,
	DeviceStatus as int_DeviceStatus}				from '../ressources/language/type';
import {Tags}										from '../ressources/components/ModalTags/interface';
import {JamfProtectData, MdmDevice,
	MdmFullDeviceData}								from '../redux/devices/interfaceMdm';
import Checkbox										from '../ressources/components/Checkbox';
import Table										from '../ressources/components/Table';

import {ft_selectDataUser,
	ft_selectPlaceholderUser}						from '../ressources/functions/selectData';
import {ft_getFunderFromContract}					from '../ressources/functions/findFromContract';
import {ft_formatOrderId, ft_formatPrice,
	ft_formatUserName}								from '../ressources/functions/format';
import {ft_genNotification, ft_pushNotification}	from '../ressources/functions/notification';
import {ft_checkDeviceFilters}						from '../ressources/functions/filters';
import {ft_getDeviceName, ft_getDeviceStatus,
	ft_getDeviceType}								from '../ressources/functions/deviceFunction';
import ft_cssVar									from '../ressources/functions/getCssVar';
import {ft_checkRight}								from '../ressources/functions/userFunction';
import {ft_search}									from '../ressources/functions/search';

import img_ticket									from '../ressources/images/icon/ticket.svg';
import img_filter									from '../ressources/images/icon/filter.svg';
import img_search									from '../ressources/images/icon/search.svg';
import img_faceSmile								from '../ressources/images/icon/face-smile.svg';
import img_dotsVertical								from '../ressources/images/icon/dots-vertical.svg';
import img_trash									from '../ressources/images/icon/trash.svg';
import img_settings									from '../ressources/images/icon/menu/settings.svg';
import img_usersPlus								from '../ressources/images/icon/users-plus.svg';
import img_deviceIcon								from '../ressources/images/icon/devices/computer.svg';
import img_arrowRight								from '../ressources/images/icon/arrow/right.svg';
import img_computer									from '../ressources/images/icon/devices/computer.svg';
import img_phone									from '../ressources/images/icon/devices/phone-2.svg';
import img_tablet									from '../ressources/images/icon/devices/tablet-2.svg';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';
import {LOCATION_TYPE, STATE}						from '../ressources/data/form';

import './Devices.css';


interface JamfOSData {
	name: string
	version: string
	update?: string
	updateV?: string
	currentPlan?: {
		status: string
		version: string
	}
}

interface Props {
	forceId?: string
}

function Devices(props: Props) {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const company = useAppSelector(state => state.company.data);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const devicesMdm = useAppSelector(state => state.devices.mdm);
	const devicesLoad = useAppSelector(state => state.devices.isLoad);
	const contracts = useAppSelector(state => state.contracts.data);
	const lang = useAppSelector(state => state.settings.lang);
	const supports = useAppSelector(state => state.support.data);
	const notificationId = useAppSelector(state => state.notifications.lastId);

	const [view, set_view] = useState('');
	const [search, set_search] = useState<InputValues>('');
	const [load, set_load] = useState(false);
	const [modal, set_modal] = useState<'removeAssign' | 'save' | 'assign' | 'delete' | 'filter' | 'command' | 'column' | null>(null);
	const [tagsList, set_tagsList] = useState<Tags[]>([]);
	const [modalAssign, set_modalAssign] = useState<false | Device>(false);

	const [modalCommand, set_modalCommand] = useState<keyof LanguageType['devices']['mdm']['actions'] | null>(null);

	const [filters, set_filters] = useState<FilterChecked[]>([]);
	const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({imei: false, imei2: false});

	const [tagsModal, set_tagsModal] = useState(false);

	// --- Device Data ---
	const [editData, set_editData] = useState<Device | undefined>(undefined);
	const [editDataFunder, set_editDataFunder] = useState<Funder | undefined>(undefined);
	const [jamfOSData, set_jamfOSData] = useState<JamfOSData | undefined>(undefined);
	const [deviceFullData, set_deviceFullData] = useState<MdmFullDeviceData>();
	const [jamfProtectData, set_jamfProtectData] = useState<JamfProtectData | undefined>(undefined);

	const [actualUser, set_actualUser] = useState<string | undefined>();
	const [purchaseType, set_purchaseType] = useState<SelectValues>(null);
	const [funder, set_funder] = useState<InputValues>('');
	const [locationStart, set_locationStart] = useState<InputValues>('');
	const [locationEnd, set_locationEnd] = useState<InputValues>('');
	const [duration, set_duration] = useState<InputValues>('');
	const [price, set_price] = useState<InputValues>('');
	const [contractNumber, set_contractNumber] = useState<InputValues>('');
	const [billingNumber, set_billingNumber] = useState<InputValues>('');
	const [name, set_name] = useState<InputValues>('');
	const [internalId, set_internalId] = useState<InputValues>('');
	const [screenSize, set_screenSize] = useState<InputValues>('');
	const [state, set_state] = useState<SelectValues>('');
	const [stockage, set_stockage] = useState<SelectValues>('');
	const [memory, set_memory] = useState<SelectValues>('');
	const [processor, set_processor] = useState<InputValues>('');
	const [graphicCard, set_graphicCard] = useState<InputValues>('');
	const [network, set_network] = useState<SelectValues>('');
	const [gpu, set_gpu] = useState<InputValues>('');
	const [keyboard, set_keyboard] = useState<SelectValues>('');
	const [definition, set_definition] = useState<InputValues>('');
	const [compatibility, set_compatibility] = useState<SelectValues[]>([]);
	const [refreshRate, set_refreshRate] = useState<InputValues>('');
	const [responseTime, set_responseTime] = useState<InputValues>('');
	const [ports, set_ports] = useState<SelectValues[]>([]);
	const [connectivity, set_connectivity] = useState<SelectValues[]>([]);
	const [subType, set_subType] = useState<SelectValues>('');
	const [batteryLife, set_batteryLife] = useState<InputValues>('');
	const [anc, set_anc] = useState<InputValues>('');
	const [stand, set_stand] = useState<SelectValues[]>([]);
	const [serial, set_serial] = useState<InputValues>('');
	const [imei, set_imei] = useState<InputValues>('');
	const [imei2, set_imei2] = useState<InputValues>('');
	const [description, set_description] = useState<InputValues>('');
	const [status, set_status] = useState<keyof int_DeviceStatus | SelectValues>();

	const [menu, set_menu] = useState<TabsValues>('details');
	const [change, set_change] = useState(false);
	// --- Device Data End ---

	const navigate = useNavigate();
	const params = useParams();
	const TEXTE = LANGUAGE(lang);
	const MENU_DATA: TabsData[] = [
		{value: 'details', render: TEXTE.global.details, right: 'company.read'},
		{value: 'characteristic', render: TEXTE.global.characteristic},
		{value: 'documents', render: TEXTE.global.documents, right: 'company.read'},
		{value: 'ticket', render: TEXTE.global.tickets}
	];

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	const connectedMdm: {
		jamf: boolean,
		vmWare: boolean,
		deviceData: (MdmDevice | null),
		mdm: (keyof DevicesState['mdm'] | null)
	} = useMemo(() => {
		set_jamfOSData(undefined);
		set_jamfProtectData(undefined);

		const checkMdm = (checkName: keyof DevicesState['mdm']) => {
			const connect = devicesMdm[checkName].find(find => find.serial === editData?.serial);

			if (connect && !mdmName) {
				mdmName = checkName;
				deviceData = connect;
			}

			if (connect && checkName === 'jamf') {
				API.getMdmDeviceSpecificDataJamf('os', connect.id, connect.serial, connect.type, user)
				.then((response) => {
					if (response.data)
						set_jamfOSData(response.data);
				});

				API.getMdmDeviceJamfProtect(connect.serial, user)
				.then((response) => {
					if (response.data)
						set_jamfProtectData(response.data);
				});

				API.getMdmDeviceAllDataJamf(connect.id, connect.serial, connect.type, user)
				.then((response) => {
					if (response.data)
						set_deviceFullData(response.data);
				});
			}

			return !!connect;
		};
		let mdmName: (string | null) = null;
		let deviceData: (MdmDevice | null) = null;

		const mdmList = {
			jamf:   checkMdm('jamf'),
			vmWare: checkMdm('vmWare')
		};

		return ({
			...mdmList,
			deviceData,
			mdm: mdmName
		});
	}, [editData, devicesMdm, modalCommand]);

	const isWin = useMemo(() =>
		connectedMdm.deviceData?.fullData?.platform?.includes('Win')
	, [connectedMdm]);

	const readonly = useMemo(() =>
		(!editData?.notValala || !ft_checkRight(user, 'company.edit'))
	, [user, editData?.notValala]);

	const columns = useMemo<ColumnDef<Device>[]>(() => [
		{
			size: 40,
			id:   'image',
			cell: v => (
				<DeviceImage
					imageFormat={'small'}
					device={v.row.original}
					className={'deviceCardImg'}
					catalog={v.row.original.shopData}
					style={{maxHeight: 30, minHeight: 30}}
				/>
			)
		},
		{
			id:         'name',
			header:     TEXTE.global.name,
			accessorFn: row => ft_getDeviceName(row) || row.shopData?.name
		},
		{
			id:         'status',
			header:     TEXTE.global.status,
			accessorFn: row => row.status,
			cell:       v => <DeviceStatus device={v.row.original} />
		},
		{
			id:         'mdm',
			header:     TEXTE.filters.deviceMdm,
			accessorFn: (row) => {
				const list: string[] = [];

				if (devicesMdm.jamf.some(f => f.serial === row.serial))
					list.push(TEXTE.component.externalApi.jamf);
				if (devicesMdm.vmWare.some(f => f.serial === row.serial))
					list.push(TEXTE.component.externalApi.vmWare);

				return list.join(', ') || -1;
			},
			cell:       (v) => {
				const list: string[] = [];

				if (devicesMdm.jamf.some(f => f.serial === v.row.original.serial))
					list.push(TEXTE.component.externalApi.jamf);
				if (devicesMdm.vmWare.some(f => f.serial === v.row.original.serial))
					list.push(TEXTE.component.externalApi.vmWare);

				if (!list.length)
					return <Badge size={'sm'} color={'error'} text={TEXTE.global.notConnected} />;
				return list.map((e, i) => <Badge key={i} size={'sm'} color={'gray'} text={e} />);
			}
		},
		{
			id:         'assign',
			accessorFn: row => ft_formatUserName(users.find(f => f?._id === row?.user)) || '',
			header:     TEXTE.global.username,
			cell:       (v) => {
				let assign = '';

				if (v.row.original?.user)
					assign = typeof(v.row.original.user) === 'string' ? v.row.original.user : v.row.original.user._id;

				return (
					<div className={'flex flex-center'} onClick={e => e.stopPropagation()}>
						{assign ?
							<Select
								value={assign}
								data={ft_selectDataUser(users)}
								onClick={() => set_modalAssign(v.row.original)}
							/>
							:
							<Badge
								color={'gray'}
								img={img_usersPlus}
								text={TEXTE.global.affect}
								onClick={() => set_modalAssign(v.row.original)}
							/>
						}
					</div>
				);
			}
		},
		{
			id:         'serial',
			header:     TEXTE.global.serial,
			accessorFn: row => row.serial || ''
		},
		{
			id:         'imei',
			header:     TEXTE.global.imei,
			accessorFn: row => row.imei || ''
		},
		{
			id:         'imei2',
			header:     `${TEXTE.global.imei}2`,
			accessorFn: row => row.imei2 || ''
		},
		{
			size:       100,
			id:         'isValala',
			header:     TEXTE.filters.supplier,
			accessorFn: row => !row?.notValala,
			cell:       v => (
				<Badge
					size={'sm'}
					color={v.row.original.notValala ? 'gray' : 'primary'}
					text={v.row.original.notValala ? TEXTE.global.notValala : TEXTE.global.valala}
				/>
			)
		},
		{
			id:         'tags',
			header:     TEXTE.global.tags,
			accessorFn: row => row?.tags?.join(' ') || '',
			cell:       v => (
				<div className={'globalDataContainerItemTags'} style={{gap: 4}}>
					{v.row.original?.tags?.map((tag, i) => {
						if (i > 1)
							return;
						if (i === 1 && (v.row.original?.tags?.length || 0) > 2)
							return <Badge key={i} color={'tags'} text={`+ ${(v.row.original?.tags?.length || 0) - 1}`} />;
						return <Badge key={i} color={'tags'} text={tag} />;
					})}
				</div>
			)
		},
		{
			id:         'companyRight_select',
			header:     TEXTE.global.action,
			cell:       (v) => {
				const data: SelectData[] = [];

				if (![...devicesMdm.jamf, ...devicesMdm.vmWare].some(f => f.serial === v.row.original.serial))
					data.push({render: 'MDM', value: 'mdm'});
				if (v.row.original?.user)
					data.push({render: TEXTE.devices.inReserve, value: 'user'});
				if (!v.row.original?.notValala)
					data.push({render: TEXTE.devices.createTicket, value: 'sav'});

				return (
					<div style={{width: 110}}>
						<Select
							data={data}
							onChange={(val) => {
								if (val === 'mdm')
									return navigate('/devices/mdm');
								if (val === 'user')
									return assignDevice(undefined, v.row.original);
								if (val === 'sav')
									return navigate(`/support/new/device/${v.row.original?._id}`);
							}}
						/>
					</div>
				);
			}
		}
	], [devicesMdm]);

	const useData = useMemo(() =>
		devicesWithLines.filter(userData => ft_checkDeviceFilters(filters, userData, users, devicesMdm))
	, [devicesWithLines, filters]);

	const table = useReactTable({
		columns,
		data:  useData,
		state: {
			columnVisibility,
			globalFilter: search
		},
		getCoreRowModel:          getCoreRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		getFilteredRowModel:      getFilteredRowModel(),
		getSortedRowModel:        getSortedRowModel(),
		getFacetedRowModel:       getFacetedRowModel(),
		getFacetedUniqueValues:   getFacetedUniqueValues(),
		getFacetedMinMaxValues:   getFacetedMinMaxValues()
	});

	useEffect(() => {
		if (props.forceId) {
			const findDevice = devicesWithLines.find(find => find?._id === props.forceId);

			setDeviceData(findDevice || null);
			return set_view(findDevice ? 'edit' : '');
		}

		dispatch(getDevicesApi(user));
		dispatch(getCompanyUsersApi(user));

		if (ft_checkRight(user, 'company.read')) {
			dispatch(getContractsApi(user));
			dispatch(getMdmDevicesApi({name: 'jamf', user}));
			dispatch(getMdmDevicesApi({name: 'vmWare', user}));
		}
	}, []);

	useEffect(() => {
		if (menu === 'ticket')
			dispatch(getSupportsApi(user));
	}, [menu]);

	useEffect(() => {
		if (props.forceId)
			return;

		if (params.idDevice === 'confirm')
			return set_view(params.idDevice);
		const findDevice = devicesWithLines.find(find => find?._id === params.idDevice);

		setDeviceData(findDevice || null);
		set_view(findDevice ? 'edit' : '');
	}, [params.idDevice, devicesWithLines, users]);

	useEffect(() => {
		if (editData)
			set_change(true);
	}, [actualUser, funder, locationStart, locationEnd, duration, price, contractNumber,
		billingNumber, screenSize, state, stockage, memory, processor, graphicCard,
		network, gpu, keyboard, description, purchaseType, serial, imei, imei2, name, status, internalId,
		anc, subType, definition, batteryLife, refreshRate, responseTime, ports, stand,
		connectivity, compatibility]
	);

	useEffect(() => set_change(false), [editData]);

	useEffect(() => set_modal(null), [actualUser]);

	useEffect(() => {
		if (tagsModal || !ft_checkRight(user, 'company.read'))
			return;

		API.getDevicesTags(user)
		.then(response => set_tagsList(response?.data || []));
	}, [tagsModal]);

	function editDevice(assignOnly = false, newAssign?: string) {
		if (load || !editData)
			return;
		set_load(true);

		if (assignOnly)
			set_actualUser(newAssign);

		const apiData: PutDeviceData = (assignOnly ?
			{
				assignOnly,
				assign: newAssign || null
			}
			:
			{
				gpu,
				anc,
				name,
				imei,
				imei2,
				stand,
				ports,
				price,
				funder,
				serial,
				subType,
				duration,
				keyboard,
				processor,
				screenSize,
				definition,
				internalId,
				graphicCard,
				batteryLife,
				refreshRate,
				description,
				responseTime,
				connectivity,
				purchaseType,
				billingNumber,
				compatibility,
				contractNumber,
				assign:	       actualUser,
				state:         state?.toString(),
				memory:        memory?.toString(),
				network:       network?.toString(),
				stockage:      stockage?.toString(),
				status:        editData.notValala ? status as string : undefined,
				locationEnd:   locationEnd ? moment(locationEnd, 'DD/MM/YYYY').toDate() : undefined,
				locationStart: locationStart ? moment(locationStart, 'DD/MM/YYYY').toDate() : undefined

			}
		);

		dispatch(putDevicesApi({idDevice: editData._id, data: apiData, user}))
		.then(() => {
			set_load(false);

			const notification = ft_genNotification(TEXTE, 'editSuccess', img_filter, editData.shopData?.name || editData.name);

			ft_pushNotification(dispatch, notificationId, notification);
			set_change(false);
			set_modal(null);
		});
	}

	function editTags(newTags: string[]) {
		if (load || !editData)
			return;
		set_load(true);

		const apiData: PutDeviceData = {
			assignOnly: true,
			tags:       newTags,
			assign:     actualUser
		};

		dispatch(putDevicesApi({idDevice: editData._id, data: apiData, user}))
		.then(() => {
			set_load(false);

			const notification = ft_genNotification(TEXTE, 'editTagsSuccess', img_filter);

			ft_pushNotification(dispatch, notificationId, notification);
			set_tagsModal(false);
			set_editData({...editData, tags: newTags});
		});
	}

	function deleteDevice() {
		if (!editData)
			return;

		dispatch(deleteDevicesApi({idDevice: editData._id, user}))
		.then(() => {
			const notification = ft_genNotification(TEXTE, 'deleteDeviceSuccess', img_settings, ft_getDeviceName(editData));
			ft_pushNotification(dispatch, notificationId, notification);

			set_modal(null);
			setDeviceData(null);
			set_view('');
		});
	}

	function setDeviceData(device: Device | null) {
		if (!device)
			return set_editData(undefined);

		const checkValue = (valueName: keyof CatalogInfo, setValue?: (value: InputValues) => void, setMultiSelect?: (val: SelectValues[]) => void, forceNotValala = false) => {
			let value;

			if ((device.notValala || forceNotValala) && device.notValalaData?.[valueName]) {
				if (valueName === 'state')
					value = STATE(TEXTE).find(find => device.notValalaData && ft_search([find.value, find.render], device.notValalaData[valueName]))?.value;
				value = value || device.notValalaData[valueName];
			} else {
				if (valueName === 'state')
					value = STATE(TEXTE).find(find => device.shopData?.info && ft_search([find.value, find.render], device.shopData?.info?.[valueName]))?.value;
				value = value || device.shopData?.info?.[valueName];
			}

			if (setMultiSelect && Array.isArray(value))
				return setMultiSelect(value);
			if (setValue && (typeof value === 'string' || !value))
				return setValue(value || '');
		};

		let contract: Contract | undefined;
		let contractFunder: Funder | undefined;

		if (typeof device.contract === 'object')
			contract = device.contract;
		else
			contract = contracts.find(find => find?._id === device.contract);

		if (contract)
			contractFunder = ft_getFunderFromContract(device._id, contract);

		set_actualUser(typeof device.user === 'object' ? device.user?._id : device.user);
		set_serial(device.serial || '');
		set_imei(device.imei || '');
		set_imei2(device.imei2 || '');

		checkValue('screenSize', set_screenSize);
		checkValue('state', set_state);
		checkValue('stockage', set_stockage);
		checkValue('memory', set_memory);
		checkValue('processor', set_processor);
		checkValue('graphicCard', set_graphicCard);
		checkValue('network', set_network);
		checkValue('gpu', set_gpu);
		checkValue('internalId', set_internalId, undefined, true);
		checkValue('anc', set_anc);
		checkValue('subType', set_subType);
		checkValue('definition', set_definition);
		checkValue('batteryLife', set_batteryLife);
		checkValue('refreshRate', set_refreshRate);
		checkValue('responseTime', set_responseTime);
		checkValue('ports', undefined, set_ports);
		checkValue('stand', undefined, set_stand);
		checkValue('connectivity', undefined, set_connectivity);
		checkValue('compatibility', undefined, set_compatibility);

		set_keyboard(device.keyboard);
		set_description(device.description || '');
		set_status(ft_getDeviceStatus(device, users));

		if (device.notValala) {
			set_name(device.name);
			set_purchaseType(device.notValalaData?.purchaseType || LOCATION_TYPE(TEXTE)[0].value);

			set_funder(device.notValalaData?.funder || '');
			set_locationStart(device.notValalaData?.startDate ? moment(device.notValalaData.startDate).format('DD/MM/YYYY') : '');
			set_locationEnd(device.notValalaData?.endDate ? moment(device.notValalaData.endDate).format('DD/MM/YYYY') : '');
			set_duration(device.notValalaData?.duration || '');
			set_price(device.notValalaData?.price || '');
			set_billingNumber(device.notValalaData?.billingNumber || '');
			set_contractNumber(device.notValalaData?.contractNumber || '');
		} else {
			set_purchaseType(LOCATION_TYPE(TEXTE)[0].value);

			set_funder('Valala');
			set_locationStart(contractFunder?.locationStart ? moment(contractFunder.locationStart).format('DD/MM/YYYY') : '');
			set_duration(contractFunder?.duration || '');
			if (contractFunder?.locationEnd)
				set_locationEnd(contractFunder?.locationEnd ? moment(contractFunder.locationEnd).format('DD/MM/YYYY') : '');
			else if (contractFunder?.locationStart && contractFunder?.duration)
				set_locationEnd(moment(contractFunder.locationStart).add(contractFunder.duration, 'months').format('DD/MM/YYYY'));
			set_price(contractFunder?.devices.find(find => find.device._id === device._id)?.price || '');
			set_billingNumber(contract?.number ? ft_formatOrderId(contract.number) : '');
			set_contractNumber(contract?.number ? ft_formatOrderId(contract.number) : '');
		}

		set_editData(device);
		set_editDataFunder(contractFunder);
	}

	function assignDevice(assignUser?: string, force?: Device) {
		if (!force && (typeof modalAssign === 'boolean' || !modalAssign?._id))
			return;

		const devicesId: string[] = [];

		if (force)
			devicesId.push(force._id);
		else if (typeof modalAssign !== 'boolean' && modalAssign?._id)
			devicesId.push(modalAssign._id);
		else
			return;

		const newUser = users.find(f => f?._id === assignUser);
		set_load(true);

		dispatch(assignDevicesApi({user, devicesId, assignUser: newUser})).then(() => {
			const notificationData = ft_genNotification(TEXTE, 'assignDeviceSuccess', img_deviceIcon, ft_formatUserName(newUser));
			ft_pushNotification(dispatch, notificationId, notificationData);

			set_load(false);
			set_modalAssign(false);
		});
	}

	function generateActionList(info = false) {
		const actionSelect = (actionName: keyof LanguageType['devices']['mdm']['actions']): SelectData => {
			let disabled = !company?.isPro || !connectedMdm.mdm;

			if (connectedMdm.mdm === 'vmWare')
				disabled = disabled || actionName === 'setConfiguration';

			return ({
				disabled,
				value:  actionName,
				render: TEXTE.devices.mdm.actions[actionName]
			});
		};
		const mdmAction = (actionName: keyof LanguageType['devices']['mdm']['actions']) => {
			const currentUpadate = actionName === 'update' && ['SchedulingScanForOSUpdates', 'CollectingAvailableOSUpdates', 'Unknown'].some(f => f === jamfOSData?.currentPlan?.status);
			let disabled = !company?.isPro || !connectedMdm.mdm || currentUpadate;

			if (connectedMdm.mdm === 'vmWare')
				disabled = disabled || actionName === 'setConfiguration';

			return (
				<Button
					size={'sm'}
					disabled={disabled}
					isLoad={currentUpadate}
					onClick={() => set_modalCommand(actionName)}
					type={actionName === 'update' ? 'secondary' : 'secondary-gray'}
				>
					{(actionName === 'compliance' && jamfProtectData?.insightsStatsFail) &&
						<Badge size={'sm'} color={'error'} text={jamfProtectData.insightsStatsFail} />
					}
					{(actionName === 'alerts' && jamfProtectData) &&
						<Badge
							size={'sm'}
							text={jamfProtectData.alerts.length}
							color={jamfProtectData.alerts.length ? 'error' : 'success'}
						/>
					}
					{TEXTE.devices.mdm.actions[actionName]}
				</Button>
			);
		};

		return (
			<div className={'flex'} style={{gap: 12, flexWrap: 'wrap'}}>
				{!info ?
					<>
						<Select
							readOnly={!company?.isPro || !connectedMdm.mdm}
							value={(!company?.isPro || !connectedMdm.mdm) ? 'lock' : undefined}
							onChange={val => set_modalCommand(val as keyof LanguageType['devices']['mdm']['actions'])}
							data={[
								// {/* {jamfOSData?.update && mdmAction('update')} */}
								actionSelect('lock'),
								(connectedMdm.mdm === 'jamf' || isWin) ? actionSelect('restart') : undefined,
								(!isWin) ? actionSelect('shutDown') : undefined,
								actionSelect('setConfiguration'),
								((connectedMdm.mdm === 'jamf' || isWin) && connectedMdm.deviceData?.type === 'computer') ? actionSelect('deleteAccount') : undefined,
								((connectedMdm.mdm === 'jamf' || isWin) && connectedMdm.deviceData?.type === 'computer') ? actionSelect('addAccount') : undefined,
								actionSelect('reset')
							]}
						/>
					</>
					:
					<>
						{(connectedMdm.mdm === 'jamf') && mdmAction('applications')}
						{jamfProtectData && mdmAction('compliance')}
						{jamfProtectData && mdmAction('alerts')}
					</>
				}
			</div>
		);
	}

	function renderDevicesStatusButton(statusButton: keyof LanguageType['filters']) {
		return (
			<Button
				fullWidth
				size={'sm'}
				onClick={() => set_filters([{key: 'status', values: [statusButton]}])}
				type={filters.some(f => f.key === 'status' && f.values.includes(statusButton)) ? 'secondary' : 'secondary-gray'}
			>
				{TEXTE.filters[statusButton]}
				<Badge size={'sm'} color={'gray'} text={devicesWithLines.filter(f => f.status === statusButton).length} />
			</Button>
		);
	}

	function renderDevicesList() {
		let filterCount = 0;

		filters.forEach(filter => filterCount += filter.values.length);

		return (
			<div className={'globalDataContainerItem flex column'}>
				<div className={'globalListHeader'}>
					<div className={'flex-row'} style={{gap: 12}}>
						<Button
							fullWidth
							size={'sm'}
							onClick={() => set_filters([])}
							type={filters.some(f => f.key === 'status') ? 'secondary-gray' : 'secondary'}
						>
							{TEXTE.global.all}
							<Badge size={'sm'} color={'gray'} text={devicesWithLines.length} />
						</Button>
						{renderDevicesStatusButton('active')}
						{renderDevicesStatusButton('stock')}
						{renderDevicesStatusButton('replacement')}
					</div>
					<div className={'flex-row'}>
						<Button
							size={'sm'}
							type={'tertiary-gray'}
							iconBefore={img_filter}
							onClick={() => set_modal('filter')}
						>
							{TEXTE.global.filter}
							{filterCount > 0 && <Badge size={'sm'} color={'gray'} text={filterCount} />}
						</Button>

						<div className={'flex'} />
						<Button
							size={'sm'}
							type={'tertiary-gray'}
							onClick={() => set_modal('column')}
						>
							{TEXTE.global.chooseColumn}
						</Button>
					</div>

					<Input
						fullWidth
						value={search}
						onChange={set_search}
						placeholder={TEXTE.devices.search}
						before={<img src={img_search} alt={TEXTE.devices.search} />}
					/>
				</div>

				<Table
					data={table}
					load={devicesLoad && devicesWithLines.length === 0}
					onClickRow={row => navigate(`/devices/${row.original?._id}`)}
				/>
			</div>
		);
	}

	function renderDefaultView() {
		return (
			<div className={'devicesSelectMain'}>
				<Icon
					size={40}
					iconBorder
					iconSize={24}
					borderSize={8}
					iconColor={view === 'confirm' ? '#000' : '#6938EF'}
					type={view === 'confirm' ? 'success' : 'primary'}
					icon={view === 'confirm' ? img_faceSmile : img_search}
				/>
				<div className={'devicesSelectTitle'}>
					{TEXTE.devices[view === 'confirm' ? 'addDeviceSuccess' : 'selectDevice']}
				</div>
				{ft_checkRight(user, 'company.edit') &&
					<>
						<div className={'devicesSelectText'}>
							{TEXTE.devices[view === 'confirm' ? 'addDeviceSuccesText' : 'selectDeviceText']}
						</div>

						<MultiInputContainer>
							<Button
								size={'lg'}
								type={'primary'}
								onClick={() => navigate('/order/catalog')}
							>
								{TEXTE.header.newCommand}
							</Button>

							<Button
								size={'lg'}
								type={'secondary-gray'}
								onClick={() => navigate('/devices/add')}
							>
								{TEXTE.devices.addDevice}
							</Button>
						</MultiInputContainer>
					</>
				}
			</div>
		);
	}

	function renderAboutSection() {
		if (!editData)
			return;

		const forceMenu = !ft_checkRight(user, 'company.read');
		let render = <></>;

		if (!forceMenu && menu === 'details') {
			render = (
				<div className={'deviceDetailsContainer'}>
					<Select
						title={TEXTE.global.type}
						data={LOCATION_TYPE(TEXTE)}
						value={purchaseType}
						onChange={set_purchaseType}
						readOnly={!editData.notValala}
						readOnlySp
					/>

					<Input
						value={funder}
						onChange={set_funder}
						title={TEXTE.global.supplier}
						readOnly={!editData.notValala}
						readOnlySp
					/>

					<MultiInputContainer>
						<Input
							type={'date'}
							value={locationStart}
							onChange={set_locationStart}
							title={purchaseType === 'lease' ? TEXTE.global.contractStart : TEXTE.global.purchaseDate}
							readOnly={!editData.notValala}
							readOnlySp
						/>
						<Input
							type={'date'}
							value={locationEnd}
							onChange={set_locationEnd}
							title={purchaseType === 'lease' ? TEXTE.global.contractEnd : TEXTE.global.guaranteeEnd}
							readOnly={!editData.notValala}
							readOnlySp
						/>
					</MultiInputContainer>

					<MultiInputContainer>
						{purchaseType === 'lease' ?
							<Input
								min={1}
								type={'number'}
								value={duration}
								onChange={set_duration}
								title={TEXTE.global.duration}
								readOnly={!editData.notValala}
								readOnlySp
								notFocusValue={duration ? `${duration} ${TEXTE.global.month}` : undefined}
							/> : <></>
						}
						<Input
							min={0}
							readOnlySp
							value={price}
							type={'number'}
							onChange={set_price}
							readOnly={!editData.notValala}
							notFocusValue={price ? ft_formatPrice(price) : undefined}
							title={purchaseType === 'lease' ? TEXTE.global.rent : TEXTE.global.purchasePriceNoTax}
						/>
					</MultiInputContainer>

					<MultiInputContainer>
						{purchaseType === 'lease' ?
							<Input
								readOnlySp
								value={contractNumber}
								onChange={set_contractNumber}
								title={TEXTE.global.contractNumber}
								readOnly={!editData.notValala}
							/>
							:
							<Input
								readOnlySp
								value={billingNumber}
								onChange={set_billingNumber}
								title={TEXTE.global.billingNumber}
								readOnly={!editData.notValala}
							/>
						}
					</MultiInputContainer>
				</div>
			);
		} else if (forceMenu || menu === 'characteristic') {
			render = (
				<div className={'deviceDetailsContainer'}>
					{editData?.notValala &&
						<Input
							value={name}
							readOnly={readonly}
							onChange={set_name}
							title={TEXTE.global.name}
						/>
					}

					<DevicesCharacteristicForm
						notValala={editData?.notValala}
						deviceType={ft_getDeviceType(editData)}
						state={state}
						set_state={set_state}
						internalId={internalId}
						set_internalId={set_internalId}
						serial={serial}
						set_serial={set_serial}
						imei={imei}
						set_imei={set_imei}
						imei2={imei2}
						set_imei2={set_imei2}
						screenSize={screenSize}
						set_screenSize={set_screenSize}
						stockage={stockage}
						set_stockage={set_stockage}
						memory={memory}
						set_memory={set_memory}
						processor={processor}
						set_processor={set_processor}
						graphicCard={graphicCard}
						set_graphicCard={set_graphicCard}
						network={network}
						set_network={set_network}
						gpu={gpu}
						set_gpu={set_gpu}
						keyboard={keyboard}
						set_keyboard={set_keyboard}
						description={description}
						set_description={set_description}
						definition={definition}
						set_definition={set_definition}
						compatibility={compatibility}
						set_compatibility={set_compatibility}
						refreshRate={refreshRate}
						set_refreshRate={set_refreshRate}
						responseTime={responseTime}
						set_responseTime={set_responseTime}
						ports={ports}
						set_ports={set_ports}
						connectivity={connectivity}
						set_connectivity={set_connectivity}
						subType={subType}
						set_subType={set_subType}
						batteryLife={batteryLife}
						set_batteryLife={set_batteryLife}
						anc={anc}
						set_anc={set_anc}
						stand={stand}
						set_stand={set_stand}
					/>
				</div>
			);
		} else if (editData && menu === 'documents') {
			render = (
				<DevicesDocumentsMenu
					editData={editData}
					editDataFunder={editDataFunder}
				/>
			);
		} else if (editData && menu === 'ticket') {
			let icon = img_computer;

			if (editData?.shopData?.type === 'phone' || editData?.notValalaData?.deviceType === 'phone')
				icon = img_phone;
			else if (editData?.shopData?.type === 'tablet' || editData?.notValalaData?.deviceType === 'tablet')
				icon = img_tablet;

			render = (
				<div className={'globalList'}>
					{supports.filter(s => s.device === editData._id).map((support, i) => (
						<div
							key={i}
							onClick={() => navigate(`/support/${support._id}`)}
							className={`userCard ${(editData?._id === support._id) && 'selected'}`}
						>
							<div className={'userCardImg'}>
								<Icon
									size={40}
									iconBorder
									icon={icon}
									borderSize={0}
									iconColor={ft_cssVar('primary-600')}
								/>
							</div>
							<div className={'contactListItemInfos'}>
								<span className={`supportStatus ${support.status}`}>{TEXTE.support.status[support.status]}</span>
								<div className={'contractListNumberContainer'}>
									<span className={'weight-500 gray-900'}>{support.number}</span>
									{moment(support.lastMessage).isAfter(moment(support.data.lastRead[0]?.date)) && <Badge text={'1'} color={'gray'} />}
								</div>
								<span className={'text-xs gray-600'}>{`${TEXTE.order.the} ${moment(support.data.creationDate).format('DD/MM/YYYY')}`}</span>
							</div>
						</div>
					))}
				</div>
			);
		} else if (editData && menu === 'historical')
			render = (<DevicesHistorical />);

		return (
			<div className={'globalDataAboutContainer'}>
				<Tabs
					data={MENU_DATA}
					onChange={set_menu}
					actual={forceMenu ? 'characteristic' : menu}
				/>

				{render}
			</div>
		);
	}

	function renderButtonContainer() {
		return (
			<ButtonStickyContainer>
				<Button
					fullWidth
					iconBefore={img_ticket}
					type={'tertiary-gray'}
					disabled={editData?.notValala}
					onClick={() => navigate(`/support/new/device/${editData?._id}`)}
				>
					{TEXTE.devices.createTicket}
				</Button>

				{ft_checkRight(user, 'company.read') &&
					<Button
						fullWidth
						disabled={!change}
						onClick={() => editDevice()}
					>
						{TEXTE.global.save}
					</Button>
				}
			</ButtonStickyContainer>
		);
	}

	function renderDeviceData() {
		if (!editData)
			return <div className={'globalDataContainer'} />;

		const renderTags: React.ReactElement[] = [];
		const statusList: SelectData[] = [
			{value: 'active', render: <DeviceStatus forceStatus={'active'} className={'text-md weight-500 gray-900'} />}
		];
		let osVersion = '';

		if (deviceFullData?.computer?.os_version)
			osVersion = `${deviceFullData.computer.os_name} ${deviceFullData.computer.os_version}`;
		else if (connectedMdm.vmWare && connectedMdm.deviceData?.osVersion)
			osVersion = connectedMdm.deviceData?.osVersion;

		if (!editData.notValala && status && status !== 'active')
			statusList.push({value: status, render: <DeviceStatus forceStatus={status as keyof int_DeviceStatus} className={'text-md weight-500 gray-900'} />});
		else if (!editData.notValala)
			statusList.push({value: 'stock', render: <DeviceStatus forceStatus={'stock'} className={'text-md weight-500 gray-900'} />});
		else {
			statusList.push(
				{value: 'stock', render: <DeviceStatus forceStatus={'stock'} className={'text-md weight-500 gray-900'} />},
				{value: 'needRepair', render: <DeviceStatus forceStatus={'needRepair'} className={'text-md weight-500 gray-900'} />},
				{value: 'repair', render: <DeviceStatus forceStatus={'repair'} className={'text-md weight-500 gray-900'} />},
				{value: 'broken', render: <DeviceStatus forceStatus={'broken'} className={'text-md weight-500 gray-900'} />}
			);
		}

		editData.tags?.forEach((tag => (
			renderTags.push(<Badge text={tag} size={'sm'} color={'tags'} />)
		)));

		return (
			<div className={`globalDataContainer ${props.forceId && 'noPaddingLeft'}`}>
				<div
					className={'deviceBackButton'}
					onClick={() => navigate('/devices')}
				>
					<Icon
						iconSize={16}
						iconColor={'#000'}
						icon={img_arrowRight}
						style={{transform: 'rotate(180deg)', width: 24, height: 24}}
					/>
					<span className={'text-md weight-500 gray-900'}>{TEXTE.global.back}</span>
				</div>

				<Section
					title={TEXTE.devices.yourDevice}
					afterTitle={
						<>
							<Badge
								size={'sm'}
								color={editData.notValala ? 'gray' : 'primary'}
								text={editData.notValala ? TEXTE.global.notValala : TEXTE.global.valala}
							/>
							{connectedMdm.mdm &&
								<Badge
									size={'sm'}
									color={'gray'}
									text={TEXTE.component.externalApi[connectedMdm.mdm]}
								/>
							}
						</>
					}
				>
					<div className={'globalDataContainerItem'}>
						<DeviceImage device={editData} className={'deviceDataHeaderImg'} imageFormat={'small'} />
						<div>
							<DeviceStatus device={editData} />
							<div className={'text-md weight-600 gray-900'}>{editData.shopData?.name || editData.name}</div>
							{osVersion &&
								<div className={'flex'} style={{gap: 6, marginTop: 6}}>
									<Badge color={'gray'} size={'sm'} text={osVersion} />
									{jamfOSData?.update &&
										<>
											{['SchedulingScanForOSUpdates', 'CollectingAvailableOSUpdates', 'Unknown'].some(f => f === jamfOSData?.currentPlan?.status) ?
												<Badge color={'warning'} size={'sm'} text={'Mise à jour en cours'}>
													<Load size={10} />
												</Badge>
												:
												<Badge color={'success'} size={'sm'} text={`Mise à jour disponible ${jamfOSData.update}`} />
											}
										</>
									}
								</div>
							}
						</div>

						<div className={'globalDataContainerItemTags'}>
							{renderTags}
						</div>

						{ft_checkRight(user, 'company.read') &&
							<Button
								absoluteRight
								absoluteHideIcon
								type={'tertiary-gray'}
								absolute={[
									{
										value:  'tags',
										render: <span className={'text-xs weight-500 gray-700'}>{TEXTE.global.editTags}</span>
									},
									{
										disabled: !editData?.notValala,
										value:    'delete',
										render:   (
											<span className={`text-xs weight-500 error-${editData?.notValala ? '6' : '2'}00`}>
												{TEXTE.global.delete}
											</span>
										)
									}
								]}
								onChangeAbsolute={(action) => {
									if (action === 'delete' && editData.notValala)
										return set_modal('delete');
									if (action === 'tags')
										return set_tagsModal(true);
								}}
							>
								<Icon icon={img_dotsVertical} />
							</Button>
						}
					</div>
				</Section>

				<Section title={TEXTE.devices.assignAndState}>
					<div className={'globalDataContainerItem'}>
						<MultiInputContainer>
							<Select
								value={actualUser}
								data={ft_selectDataUser(users)}
								onClick={() => set_modal('assign')}
								placeholder={ft_selectPlaceholderUser(TEXTE)}
								readOnly={!ft_checkRight(user, 'company.read')}
							/>
							<Select
								value={status}
								data={statusList}
								readOnly={(!editData.notValala && status !== 'active') || !ft_checkRight(user, 'company.read')}
								onChange={newValues => (editData.notValala ? set_status(newValues) : set_modal('removeAssign'))}
							/>
						</MultiInputContainer>
					</div>
				</Section>

				{ft_checkRight(user, 'company.read') &&
					<>
						<Section
							title={'Actions à distance avec votre MDM'}
							afterTitle={
								(!company?.isPro ?
									<Badge size={'sm'} color={'gray'} text={'Pro'} />
									:
									<Badge
										size={'sm'}
										color={connectedMdm.mdm ? 'success' : 'primary'}
										text={connectedMdm.mdm ? TEXTE.component.externalApi[connectedMdm.mdm] : TEXTE.global.notConnected}
									/>
								)
							}
						>
							{generateActionList()}
						</Section>

						{(connectedMdm.mdm === 'jamf' || jamfProtectData) &&
							<Section title={'Informations de l\'appareil via MDM'}>
								{generateActionList(true)}
							</Section>
						}
					</>
				}

				<Section title={TEXTE.global.about}>
					{renderAboutSection()}
				</Section>

				{renderButtonContainer()}
			</div>
		);
	}

	function renderModal() {
		if (modal === 'delete') {
			if (!editData)
				return;

			return (
				<Modal
					confirmButton={deleteDevice}
					onClose={() => set_modal(null)}
					cancelButton={() => set_modal(null)}
					header={
						<Icon
							size={40}
							iconBorder
							iconSize={18}
							borderSize={8}
							type={'warning'}
							icon={img_trash}
							iconColor={ft_cssVar('warning-600')}
						/>
					}
				>
					<span className={'text-lg weight-600 gray-900'}>{TEXTE.devices.modal.deleteDevice}</span>
					<span className={'text-sm gray-600'}>
						{TEXTE.devices.modal.deleteDeviceTextPart1}
						<b>{ft_getDeviceName(editData)}</b>
						{TEXTE.devices.modal.deleteDeviceTextPart2}
					</span>
				</Modal>
			);
		}

		if (modal === 'removeAssign') {
			return (
				<Modal
					header={<IconModalSave />}
					onClose={() => set_modal(null)}
					cancelButton={() => set_modal(null)}
					confirmButton={() => editDevice(true)}
				>
					<span className={'modalTitle'}>
						{TEXTE.devices.modal.removeAssignSave}
					</span>
					<span>
						{TEXTE.devices.modal.removeAssignConfirm}
					</span>
				</Modal>
			);
		}

		if (modal === 'filter') {
			return (
				<ModalFilter
					filters={filters}
					tagsList={tagsList}
					devicesMdm={devicesMdm}
					setFilters={set_filters}
					companyIsPro={company?.isPro}
					onClose={() => set_modal(null)}
				/>
			);
		}

		if (modal === 'column') {
			const render: React.ReactElement[] = [];

			table.getAllLeafColumns().map((column) => {
				if (!column.columnDef.header || typeof column.columnDef.header !== 'string')
					return;

				render.push(
					<label
						key={column.id}
						className={'modalFilterItem'}
					>
						<Checkbox active={column.getIsVisible()} />
						<span>{column.columnDef.header}</span>
						<input
							type={'checkbox'}
							style={{visibility: 'hidden'}}
							{...{
								checked:  column.getIsVisible(),
								onChange: column.getToggleVisibilityHandler()
							}}
						/>
					</label>
				);
			});

			return (
				<Modal
					modalRight
					onClose={() => set_modal(null)}
				>
					<div className={'modalFilterContainer'}>
						<span>{TEXTE.global.chooseColumn}</span>
						{render}
					</div>
				</Modal>
			);
		}

		if (modalCommand && connectedMdm.mdm) {
			return (
				<ModalMdmCommand
					isWin={isWin}
					editData={editData}
					mdmName={connectedMdm.mdm}
					modalCommand={modalCommand}
					jamfProtect={jamfProtectData}
					onClose={() => set_modalCommand(null)}
					version={modalCommand === 'update' ? jamfOSData?.updateV : undefined}
				/>
			);
		}

		return (
			<ModalAssign
				modalRight
				onClose={() => set_modal(null)}
				onConfirm={val => editDevice(true, val)}
				actualId={actualUser ? [actualUser] : []}
			/>
		);
	}

	return (
		<div className={'globalMainContainer'}>
			{(!props.forceId && !view) && renderDevicesList()}

			{(view === 'confirm') && renderDefaultView()}
			{(view === 'edit') && renderDeviceData()}

			{(modal || modalCommand) && renderModal()}

			{(tagsModal && editData) &&
				<ModalTags
					load={load}
					allTags={tagsList}
					onConfirm={editTags}
					tags={editData.tags || []}
					onClose={() => set_tagsModal(false)}
				/>
			}

			{modalAssign &&
				<ModalAssign
					modalRight
					load={load}
					onConfirm={assignDevice}
					onClose={() => set_modalAssign(false)}
					actualId={typeof modalAssign.user === 'string' ? [modalAssign.user] : [modalAssign.user?._id || '']}
				/>
			}
		</div>
	);
}

export default Devices;
